import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Shield, Clock, Users, Star, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const LandingPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentProcessSlide, setCurrentProcessSlide] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: '',
  });

  const countryCodes = [
    { code: '+1', country: 'US/Canada' },
    { code: '+44', country: 'UK' },
    { code: '+91', country: 'India' },
    { code: '+86', country: 'China' },
    { code: '+81', country: 'Japan' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+39', country: 'Italy' },
    { code: '+61', country: 'Australia' },
    { code: '+971', country: 'UAE' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!/^\d{10}$/.test(formData.phone)) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }

    console.log('Form submitted:', formData);
    toast.success('Thank you! We will contact you soon.');
    
    setFormData({
      name: '',
      email: '',
      countryCode: '+1',
      phone: '',
      message: '',
    });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: value });
  };

  const handleChatClick = () => {
    toast.info('Chat feature will be integrated soon!');
  };

  const testimonials = [
    {
      name: 'Jennifer Martinez',
      role: 'E-commerce Business Owner',
      image: 'https://images.unsplash.com/photo-1655249493799-9cee4fe983bb',
      content: 'Securing my trademark through Legal Flick was seamless. They handled everything professionally and kept me informed throughout the entire process.',
      rating: 5,
    },
    {
      name: 'Robert Chang',
      role: 'Tech Startup Founder',
      image: 'https://images.unsplash.com/photo-1584940120505-117038d90b05',
      content: 'Best decision I made for my startup. My brand is now protected, and I can focus on growing my business with peace of mind.',
      rating: 5,
    },
    {
      name: 'Amanda Foster',
      role: 'Creative Agency Director',
      image: 'https://images.unsplash.com/photo-1655249481446-25d575f1c054',
      content: 'The trademark monitoring service is invaluable. I know my brand is protected 24/7. Highly recommended!',
      rating: 5,
    },
    {
      name: 'Marcus Johnson',
      role: 'Retail Business Owner',
      image: 'https://images.unsplash.com/photo-1765248148309-69d900a5bc17',
      content: 'Professional, affordable, and fast. Legal Flick made trademark registration simple and stress-free.',
      rating: 5,
    },
  ];

  const processSteps = [
    {
      title: 'Free Consultation',
      description: 'Discuss your trademark needs with our experts. We analyze your brand and recommend the best protection strategy.',
    },
    {
      title: 'Comprehensive Search',
      description: 'We conduct a thorough trademark search to ensure your mark is available and identify potential conflicts.',
    },
    {
      title: 'Application Filing',
      description: 'Our team prepares and files your trademark application with the USPTO, ensuring all details are accurate.',
    },
    {
      title: 'Ongoing Monitoring',
      description: 'We monitor your trademark status and alert you to any potential infringements or required actions.',
    },
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Brand Protection',
      description: 'Secure exclusive rights to your brand name, logo, and identity nationwide.',
    },
    {
      icon: CheckCircle,
      title: 'Legal Peace of Mind',
      description: 'Attorney-backed services ensure your trademark is properly registered and protected.',
    },
    {
      icon: Clock,
      title: 'Fast Processing',
      description: 'We expedite your application and keep you updated at every step of the process.',
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: '24/7 customer support and access to legal professionals whenever you need assistance.',
    },
  ];

  // Auto-slide testimonials
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  // Auto-slide process steps
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentProcessSlide((prev) => (prev + 1) % processSteps.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [processSteps.length]);

  return (
    <div className="min-h-screen bg-white">
      <Header showNav={false} onChatClick={handleChatClick} />

      {/* Hero Section with Form */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-6">
                Protect Your Brand Today
              </div>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Secure Your Trademark in 3 Simple Steps
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Professional trademark registration services starting at just $49 + USPTO fees. Attorney-backed protection for your brand.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Comprehensive Trademark Search</div>
                    <div className="text-gray-600">Ensure your mark is available before filing</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Expert Application Filing</div>
                    <div className="text-gray-600">Professional preparation and submission to USPTO</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Ongoing Monitoring</div>
                    <div className="text-gray-600">Track your trademark status and protect your rights</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className="bg-white rounded-lg shadow-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get Started Today</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <Input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
                  <div className="flex space-x-2">
                    <Select
                      value={formData.countryCode}
                      onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code}>
                            {item.code} {item.country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="1234567890"
                      maxLength={10}
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">10 digits only</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your trademark needs..."
                    rows={4}
                  />
                </div>

                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg">
                  Register My Trademark
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Register Your Trademark?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protect your brand identity and secure exclusive rights to your business name and logo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <Card key={idx} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <benefit.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section with Slider */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Trademark Registration Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, transparent, and professional. We guide you through every step.
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <Card className="overflow-hidden">
              <CardContent className="p-12">
                <div className="text-center">
                  <div className="text-6xl font-bold text-blue-100 mb-4">
                    0{currentProcessSlide + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {processSteps[currentProcessSlide].title}
                  </h3>
                  <p className="text-gray-600 text-lg">
                    {processSteps[currentProcessSlide].description}
                  </p>
                </div>
              </CardContent>
            </Card>
            <div className="flex justify-center mt-6 space-x-2">
              {processSteps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentProcessSlide(idx)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    idx === currentProcessSlide ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Start Your Application
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Transparent, Affordable Pricing
            </h2>
            <p className="text-xl text-gray-600">
              No hidden fees. No surprises. Just professional trademark protection.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Basic</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">$49</span>
                  <span className="text-gray-600"> + USPTO fees</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Trademark application filing</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Basic trademark search</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Email support</span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Get Legal Help
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-600 hover:shadow-xl transition-shadow relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Most Popular
              </div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Professional</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">$149</span>
                  <span className="text-gray-600"> + USPTO fees</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Everything in Basic</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Comprehensive trademark search</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Attorney review</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Priority support</span>
                  </li>
                </ul>
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Get Legal Help
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Premium</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">$299</span>
                  <span className="text-gray-600"> + USPTO fees</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Everything in Professional</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Ongoing trademark monitoring</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Attorney consultation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">24/7 priority support</span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Get Legal Help
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Thousands
            </h2>
            <p className="text-xl text-gray-600">
              See what our clients say about our trademark services
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <img
                    src={testimonials[currentSlide].image}
                    alt={testimonials[currentSlide].name}
                    className="w-20 h-20 rounded-full object-cover mx-auto mb-6"
                  />
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonials[currentSlide].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-xl text-gray-700 mb-6 italic">
                    "{testimonials[currentSlide].content}"
                  </p>
                  <div className="font-semibold text-gray-900">{testimonials[currentSlide].name}</div>
                  <div className="text-gray-600">{testimonials[currentSlide].role}</div>
                </div>
              </CardContent>
            </Card>
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    idx === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Don't Wait – Protect Your Brand Today
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Every day without trademark protection puts your brand at risk. Start your application now and secure your business future.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Register My Trademark
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-blue-700 text-lg px-8 py-6"
              onClick={handleChatClick}
            >
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;